import { WorkOrderItemData } from '../index';
import { message } from 'antd';
import { useDisclosure } from '@/hooks';
import { useWorkorderItemPermissions } from '../../WorkOrderItemPartForm/workorderItemPermissions';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import WorkOrderItemSpan from '../WorkOrderItemSpan';
import { quantityFormatter } from '@/utils/number';
import {
  WorkOrderComplement,
  WorkOrderComplementStatus,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import InputDecimalCell from '@/components/_common/InputDecimalCell';
import useWorkOrderItemUpdate from '../WorkOrderItemUtils/useWorkOrderItemUpdate';
import { WorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { isAppPortocar } from '@/index';
import {
  getBenefitMaxQuantity,
  getBenefitQuantityFractions,
  isQuantityForbiddenDecimal,
  isQuantityOverLimit,
} from '../../../Complements/ComplementPolicyBenefits/constants';

interface Props {
  item: WorkOrderItemData;
  workOrderOverride?: WorkOrder;
  complementOverride?: WorkOrderComplement;
}

export default function WorkOrderItemQuantity(props: Props) {
  const [isInputEnabled, inputAction] = useDisclosure(false);
  const itemPermissions = useWorkorderItemPermissions();

  const detailCtx = useWorkorderDetailContext();
  const complement =
    props.complementOverride ?? detailCtx?.complement?.data;

  const update = useWorkOrderItemUpdate(props.item, {
    workOrderOverride: props.workOrderOverride,
    complementOverride: props.complementOverride,
  });

  const benefitServiceCodeNumber = props.item.benefitServiceCode
    ? Number(props.item.benefitServiceCode)
    : undefined;
  const isBenefitItem =
    isAppPortocar &&
    typeof benefitServiceCodeNumber === 'number' &&
    !Number.isNaN(benefitServiceCodeNumber);
  const parentBenefit = isBenefitItem
    ? { serviceCode: benefitServiceCodeNumber }
    : undefined;
  const benefitMaxQuantity = isBenefitItem
    ? getBenefitMaxQuantity(parentBenefit)
    : undefined;
  const benefitFractions = isBenefitItem
    ? getBenefitQuantityFractions(parentBenefit)
    : undefined;

  const handleUpdateQuantity = async (quantity: number) => {
    if (isBenefitItem) {
      if (isQuantityForbiddenDecimal(parentBenefit, quantity)) {
        message.destroy();
        message.error(
          'Materiais de troca de lâmpada não aceitam casas decimais.',
        );
        inputAction.close();
        return;
      }
      if (isQuantityOverLimit(parentBenefit, quantity)) {
        message.destroy();
        message.error(
          'Quantidade acima do limite permitido para o benefício.',
        );
        inputAction.close();
        return;
      }
    }

    if (!itemPermissions.allowSellingBellowCostValue) {
      const subTotal = (quantity || 0) * (props.item?.unitPrice || 0);

      const amount = subTotal - (props.item?.discount || 0) * 100;

      if (amount < (props.item?.unitCost || 0)) {
        message.destroy();
        return message.warn(
          'O preço de venda deve ser maior que o custo unitário',
        );
      }
    }

    try {
      await update.mutateAsync({
        key: 'quantity',
        value: quantity <= 0 ? 1 : quantity,
      });
    } finally {
      inputAction.close();
    }
  };

  if (isInputEnabled) {
    return (
      <InputDecimalCell
        defaultValue={props.item.quantity}
        min={0}
        max={benefitMaxQuantity}
        fractions={benefitFractions}
        loading={update.isLoading}
        onConfirmValue={(value) => handleUpdateQuantity(value)}
        onClose={inputAction.close}
      />
    );
  }

  return (
    <WorkOrderItemSpan
      item={props.item}
      onClick={inputAction.open}
      complementOverride={props.complementOverride}
      permission={
        (complement?.status !== WorkOrderComplementStatus.Open &&
          !hasPermissions([
            Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_QUANTITY,
          ])) ||
        !hasPermissions([
          Authorities.MOVIMENTATION_WORKORDER_ITEM_CHANGE_QUANTITY,
        ])
      }
    >
      {props.item.quantity ? quantityFormatter(props.item.quantity) : 0}
    </WorkOrderItemSpan>
  );
}
